import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { 
  Palette, Plus, Edit2, Trash2, Eye, EyeOff, Save, X, 
  Loader2, RefreshCw, Check, Copy
} from 'lucide-react';
import Sidebar from '../components/Sidebar';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';

const COLOR_FIELDS = [
  { key: 'primary_color', label: 'Primary Color', description: 'Main brand color' },
  { key: 'secondary_color', label: 'Secondary Color', description: 'Supporting brand color' },
  { key: 'accent_color', label: 'Accent Color', description: 'Highlights and CTAs' },
  { key: 'sidebar_bg', label: 'Sidebar Background', description: 'Admin sidebar background' },
  { key: 'sidebar_text', label: 'Sidebar Text', description: 'Admin sidebar text color' },
  { key: 'sidebar_link_active', label: 'Sidebar Link Active', description: 'Selected menu item background' },
  { key: 'sidebar_link_hover', label: 'Sidebar Link Hover', description: 'Hovered menu item background' },
  { key: 'external_header_bg', label: 'External Header BG', description: 'Public pages header' },
  { key: 'external_header_text', label: 'External Header Text', description: 'Public pages header text' },
  { key: 'external_page_bg', label: 'External Page BG', description: 'Public pages background' },
  { key: 'external_footer_bg', label: 'External Footer BG', description: 'Public pages footer' },
  { key: 'external_footer_text', label: 'External Footer Text', description: 'Public pages footer text' },
];

const DEFAULT_COLORS = {
  primary_color: '#2B4B6A',
  secondary_color: '#64748b',
  accent_color: '#3b82f6',
  sidebar_bg: '#1e293b',
  sidebar_text: '#e2e8f0',
  sidebar_link_active: '#1E3A52',
  sidebar_link_hover: '#3D5F80',
  external_header_bg: '#FFFFFF',
  external_header_text: '#2B4B6A',
  external_page_bg: '#f8f9fa',
  external_footer_bg: '#FFFFFF',
  external_footer_text: '#64748b'
};

const AdminThemePresets = () => {
  const { user } = useAuth();
  const [presets, setPresets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // Edit modal state
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingPreset, setEditingPreset] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    key: '',
    description: '',
    colors: { ...DEFAULT_COLORS },
    is_active: true
  });
  
  // Delete confirmation
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const isMainAdmin = user?.role === 'admin' || user?.role === 'main_admin';

  useEffect(() => {
    fetchPresets();
  }, []);

  const fetchPresets = async () => {
    try {
      const res = await api.get('/admin/theme-presets');
      setPresets(res.data);
    } catch (error) {
      toast.error('Failed to load theme presets');
    } finally {
      setLoading(false);
    }
  };

  const openCreateModal = () => {
    setEditingPreset(null);
    setFormData({
      name: '',
      key: '',
      description: '',
      colors: { ...DEFAULT_COLORS },
      is_active: true
    });
    setShowEditModal(true);
  };

  const openEditModal = (preset) => {
    setEditingPreset(preset);
    setFormData({
      name: preset.name,
      key: preset.key,
      description: preset.description || '',
      colors: { ...preset.colors },
      is_active: preset.is_active
    });
    setShowEditModal(true);
  };

  const handleSave = async () => {
    if (!formData.name.trim()) {
      toast.error('Theme name is required');
      return;
    }
    if (!editingPreset && !formData.key.trim()) {
      toast.error('Theme key is required');
      return;
    }
    
    setSaving(true);
    try {
      if (editingPreset) {
        await api.put(`/admin/theme-presets/${editingPreset.id}`, {
          name: formData.name,
          description: formData.description,
          colors: formData.colors,
          is_active: formData.is_active
        });
        toast.success('Theme preset updated');
      } else {
        await api.post('/admin/theme-presets', formData);
        toast.success('Theme preset created');
      }
      setShowEditModal(false);
      fetchPresets();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save theme preset');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (preset) => {
    try {
      await api.post(`/admin/theme-presets/${preset.id}/toggle`);
      toast.success(`Theme ${preset.is_active ? 'deactivated' : 'activated'}`);
      fetchPresets();
    } catch (error) {
      toast.error('Failed to toggle theme status');
    }
  };

  const handleDelete = async (presetId) => {
    try {
      await api.delete(`/admin/theme-presets/${presetId}`);
      toast.success('Theme preset deleted');
      setDeleteConfirm(null);
      fetchPresets();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete theme preset');
    }
  };

  const handleResetDefaults = async () => {
    if (!window.confirm('This will reset all default themes to their original colors. Continue?')) return;
    
    try {
      await api.post('/admin/theme-presets/reset-defaults');
      toast.success('Default themes reset');
      fetchPresets();
    } catch (error) {
      toast.error('Failed to reset defaults');
    }
  };

  const handleColorChange = (key, value) => {
    setFormData({
      ...formData,
      colors: { ...formData.colors, [key]: value }
    });
  };

  const duplicatePreset = (preset) => {
    setEditingPreset(null);
    setFormData({
      name: `${preset.name} (Copy)`,
      key: `${preset.key}_copy_${Date.now()}`,
      description: preset.description || '',
      colors: { ...preset.colors },
      is_active: true
    });
    setShowEditModal(true);
  };

  if (loading) {
    return (
      <Sidebar>
        <div className="p-8 flex items-center justify-center min-h-screen">
          <Loader2 className="w-8 h-8 animate-spin text-orange-600" />
        </div>
      </Sidebar>
    );
  }

  return (
    <Sidebar>
      <div className="p-8 bg-gray-50/50 min-h-screen">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Theme Presets</h1>
            <p className="text-gray-500 mt-1">Manage color themes available to companies</p>
          </div>
          
          <div className="flex items-center gap-3">
            <button
              onClick={handleResetDefaults}
              className="flex items-center gap-2 px-4 py-2.5 border border-gray-200 rounded-xl text-gray-600 hover:bg-gray-50 transition-colors"
            >
              <RefreshCw className="w-4 h-4" />
              Reset Defaults
            </button>
            <button
              onClick={openCreateModal}
              className="flex items-center gap-2 px-4 py-2.5 bg-orange-600 text-white rounded-xl hover:bg-orange-700 transition-colors"
              data-testid="create-theme-btn"
            >
              <Plus className="w-4 h-4" />
              New Theme
            </button>
          </div>
        </div>

        {/* Presets Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {presets.map(preset => (
            <div
              key={preset.id}
              className={`bg-white rounded-2xl border overflow-hidden transition-shadow hover:shadow-lg ${
                preset.is_active ? 'border-gray-200' : 'border-gray-100 opacity-60'
              }`}
            >
              {/* Color Preview Bar */}
              <div className="h-3 flex">
                <div className="flex-1" style={{ backgroundColor: preset.colors.primary_color }} />
                <div className="flex-1" style={{ backgroundColor: preset.colors.secondary_color }} />
                <div className="flex-1" style={{ backgroundColor: preset.colors.accent_color }} />
                <div className="flex-1" style={{ backgroundColor: preset.colors.sidebar_bg }} />
              </div>
              
              <div className="p-5">
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <div className="flex items-center gap-2">
                      <h3 className="font-semibold text-gray-900">{preset.name}</h3>
                      {preset.is_default && (
                        <span className="text-xs px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full">Default</span>
                      )}
                    </div>
                    <p className="text-xs text-gray-400 mt-0.5">Key: {preset.key}</p>
                  </div>
                  
                  <button
                    onClick={() => handleToggleActive(preset)}
                    className={`p-1.5 rounded-lg transition-colors ${
                      preset.is_active 
                        ? 'text-green-600 hover:bg-green-50' 
                        : 'text-gray-400 hover:bg-gray-100'
                    }`}
                    title={preset.is_active ? 'Active - Click to deactivate' : 'Inactive - Click to activate'}
                  >
                    {preset.is_active ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                  </button>
                </div>
                
                {preset.description && (
                  <p className="text-sm text-gray-500 mb-4 line-clamp-2">{preset.description}</p>
                )}
                
                {/* Sidebar Preview */}
                <div className="mb-4 rounded-lg overflow-hidden border border-gray-100">
                  <div 
                    className="p-3 flex items-center gap-2"
                    style={{ backgroundColor: preset.colors.sidebar_bg }}
                  >
                    <Palette className="w-4 h-4" style={{ color: preset.colors.sidebar_text }} />
                    <span className="text-sm font-medium" style={{ color: preset.colors.sidebar_text }}>
                      Sidebar Preview
                    </span>
                  </div>
                  <div className="flex">
                    <div 
                      className="flex-1 p-2 text-xs text-center"
                      style={{ 
                        backgroundColor: preset.colors.sidebar_link_active,
                        color: preset.colors.sidebar_text 
                      }}
                    >
                      Active
                    </div>
                    <div 
                      className="flex-1 p-2 text-xs text-center"
                      style={{ 
                        backgroundColor: preset.colors.sidebar_link_hover,
                        color: preset.colors.sidebar_text 
                      }}
                    >
                      Hover
                    </div>
                  </div>
                </div>
                
                {/* Color Swatches */}
                <div className="flex flex-wrap gap-1.5 mb-4">
                  {Object.entries(preset.colors).slice(0, 7).map(([key, color]) => (
                    <div
                      key={key}
                      className="w-6 h-6 rounded border border-gray-200"
                      style={{ backgroundColor: color }}
                      title={`${key}: ${color}`}
                    />
                  ))}
                </div>
                
                {/* Actions */}
                <div className="flex items-center gap-2 pt-3 border-t border-gray-100">
                  <button
                    onClick={() => openEditModal(preset)}
                    className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    <Edit2 className="w-4 h-4" />
                    Edit
                  </button>
                  <button
                    onClick={() => duplicatePreset(preset)}
                    className="flex items-center justify-center px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                    title="Duplicate"
                  >
                    <Copy className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => setDeleteConfirm(preset.id)}
                    className="flex items-center justify-center px-3 py-2 text-sm text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                    title="Delete"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Edit Modal */}
        {showEditModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
            <div className="bg-white rounded-2xl w-full max-w-3xl my-8">
              <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
                <h2 className="text-xl font-semibold">
                  {editingPreset ? 'Edit Theme Preset' : 'Create New Theme'}
                </h2>
                <button
                  onClick={() => setShowEditModal(false)}
                  className="p-1 text-gray-400 hover:text-gray-600"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              <div className="p-6 max-h-[70vh] overflow-y-auto">
                {/* Basic Info */}
                <div className="grid grid-cols-2 gap-4 mb-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Theme Name *</label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData({...formData, name: e.target.value})}
                      placeholder="e.g., Corporate Blue"
                      className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Theme Key {!editingPreset && '*'}
                    </label>
                    <input
                      type="text"
                      value={formData.key}
                      onChange={(e) => setFormData({...formData, key: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '_')})}
                      placeholder="e.g., corporate_blue"
                      className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500"
                      disabled={!!editingPreset}
                    />
                    {!editingPreset && (
                      <p className="text-xs text-gray-400 mt-1">Lowercase letters, numbers, and underscores only</p>
                    )}
                  </div>
                </div>
                
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                  <input
                    type="text"
                    value={formData.description}
                    onChange={(e) => setFormData({...formData, description: e.target.value})}
                    placeholder="Brief description of this theme"
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500"
                  />
                </div>
                
                {/* Color Fields */}
                <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                  <Palette className="w-4 h-4" />
                  Theme Colors
                </h3>
                
                <div className="grid grid-cols-3 gap-4">
                  {COLOR_FIELDS.map(field => (
                    <div key={field.key} className="group">
                      <label className="block text-sm font-medium text-gray-700 mb-1">{field.label}</label>
                      <div className="flex items-center gap-2">
                        <div 
                          className="w-10 h-10 rounded-lg border border-gray-200 flex-shrink-0 cursor-pointer relative overflow-hidden"
                          style={{ backgroundColor: formData.colors[field.key] }}
                        >
                          <input
                            type="color"
                            value={formData.colors[field.key]}
                            onChange={(e) => handleColorChange(field.key, e.target.value)}
                            className="absolute inset-0 opacity-0 cursor-pointer w-full h-full"
                          />
                        </div>
                        <input
                          type="text"
                          value={formData.colors[field.key]}
                          onChange={(e) => handleColorChange(field.key, e.target.value)}
                          className="flex-1 px-3 py-2 border border-gray-200 rounded-lg text-sm font-mono focus:outline-none focus:ring-2 focus:ring-orange-500"
                        />
                      </div>
                      <p className="text-xs text-gray-400 mt-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                        {field.description}
                      </p>
                    </div>
                  ))}
                </div>
                
                {/* Preview */}
                <div className="mt-6 p-4 bg-gray-50 rounded-xl">
                  <h4 className="text-sm font-medium text-gray-700 mb-3">Preview</h4>
                  <div className="flex gap-4">
                    {/* Sidebar Preview */}
                    <div className="w-48 rounded-lg overflow-hidden border border-gray-200">
                      <div 
                        className="p-3"
                        style={{ backgroundColor: formData.colors.sidebar_bg }}
                      >
                        <div className="flex items-center gap-2 mb-3">
                          <div className="w-6 h-6 rounded bg-white/20" />
                          <span className="text-sm font-medium" style={{ color: formData.colors.sidebar_text }}>
                            Company
                          </span>
                        </div>
                        <div 
                          className="px-3 py-2 rounded-lg mb-1 text-sm"
                          style={{ 
                            backgroundColor: formData.colors.sidebar_link_active,
                            color: formData.colors.sidebar_text 
                          }}
                        >
                          Dashboard
                        </div>
                        <div 
                          className="px-3 py-2 rounded-lg mb-1 text-sm"
                          style={{ 
                            backgroundColor: formData.colors.sidebar_link_hover,
                            color: formData.colors.sidebar_text 
                          }}
                        >
                          Users
                        </div>
                        <div 
                          className="px-3 py-2 text-sm"
                          style={{ color: formData.colors.sidebar_text }}
                        >
                          Settings
                        </div>
                      </div>
                    </div>
                    
                    {/* Page Preview */}
                    <div className="flex-1 rounded-lg overflow-hidden border border-gray-200">
                      <div 
                        className="px-4 py-2 flex items-center justify-between"
                        style={{ 
                          backgroundColor: formData.colors.external_header_bg,
                          color: formData.colors.external_header_text 
                        }}
                      >
                        <span className="font-semibold text-sm">Header</span>
                        <div 
                          className="px-3 py-1 rounded text-xs text-white"
                          style={{ backgroundColor: formData.colors.primary_color }}
                        >
                          Primary
                        </div>
                      </div>
                      <div 
                        className="p-4"
                        style={{ backgroundColor: formData.colors.external_page_bg }}
                      >
                        <div className="flex gap-2 mb-2">
                          <div 
                            className="px-3 py-1 rounded text-xs text-white"
                            style={{ backgroundColor: formData.colors.secondary_color }}
                          >
                            Secondary
                          </div>
                          <div 
                            className="px-3 py-1 rounded text-xs text-white"
                            style={{ backgroundColor: formData.colors.accent_color }}
                          >
                            Accent
                          </div>
                        </div>
                        <div className="h-8 bg-white rounded border border-gray-200" />
                      </div>
                      <div 
                        className="px-4 py-2 text-xs"
                        style={{ 
                          backgroundColor: formData.colors.external_footer_bg,
                          color: formData.colors.external_footer_text 
                        }}
                      >
                        Footer text here
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Active Toggle */}
                <label className="flex items-center gap-3 mt-4 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.is_active}
                    onChange={(e) => setFormData({...formData, is_active: e.target.checked})}
                    className="w-4 h-4 rounded border-gray-300 text-orange-600 focus:ring-orange-500"
                  />
                  <span className="text-sm text-gray-700">Theme is active and available to companies</span>
                </label>
              </div>
              
              <div className="px-6 py-4 border-t border-gray-100 flex justify-end gap-3">
                <button
                  onClick={() => setShowEditModal(false)}
                  className="px-5 py-2.5 border border-gray-200 rounded-xl text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSave}
                  disabled={saving}
                  className="flex items-center gap-2 px-5 py-2.5 bg-orange-600 text-white rounded-xl hover:bg-orange-700 disabled:opacity-50"
                >
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                  {editingPreset ? 'Save Changes' : 'Create Theme'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Delete Confirmation */}
        {deleteConfirm && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl w-full max-w-sm p-6 text-center">
              <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Trash2 className="w-6 h-6 text-red-600" />
              </div>
              <h3 className="text-lg font-semibold mb-2">Delete Theme Preset?</h3>
              <p className="text-gray-500 text-sm mb-6">
                This theme will no longer be available to companies. This cannot be undone.
              </p>
              <div className="flex gap-3">
                <button
                  onClick={() => setDeleteConfirm(null)}
                  className="flex-1 px-4 py-2.5 border border-gray-200 rounded-xl text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={() => handleDelete(deleteConfirm)}
                  className="flex-1 px-4 py-2.5 bg-red-600 text-white rounded-xl hover:bg-red-700"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Sidebar>
  );
};

export default AdminThemePresets;
